import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Storage } from '@ionic/storage-angular';

@Injectable({
  providedIn: 'root',
})
export class CacherService<T> {
  private cache = new Map<string, { data: T; ttl: number }>();
  public cache$ = new BehaviorSubject<T | null>(null);

  constructor(private storage: Storage) {}

  async set(key: string, data: T, TTL: number = 120): Promise<void> {
    const expirationTime = TTL > 0 ? Date.now() + TTL * 60 * 1000 : 0;
    this.cache.set(key, { data, ttl: expirationTime });
    await this.storage.set(key, { data, ttl: expirationTime });
    this.cache$.next(data);
  }

  async get(key: string): Promise<T | null> {
    let cached = this.cache.get(key);

    if (!cached) {
      const stored = await this.storage.get(key);
      if (stored) {
        cached = stored;
        this.cache.set(key, cached);
      } else {
        return null;
      }
    }
    const { data, ttl } = cached;
    if (ttl && Date.now() > ttl) {
      this.cache.delete(key);
      this.cache$.next(null);
      await this.storage.remove(key);
      return null;
    }

    this.cache$.next(data);
    return data;
  }

  public async clearKey(key:string): Promise<void> {
    this.cache.delete(key);
    await this.storage.remove(key);
    this.cache$.next(null);
  }



  async clear(): Promise<void> {
    this.cache.clear();
    await this.storage.clear();
    this.cache$.next(null);
  }

  memoize(fn: (...args: any[]) => T, ttl: number): (...args: any[]) => T {
    return (...args: any[]): T => {
      const key = JSON.stringify(args);
      if (!this.cache.has(key)) {
        const result = fn(...args);
        this.set(key, result, ttl);
      }
      return this.get(key) as T;
    };
  }
}
